import React, { useState, useEffect, useCallback, useRef } from 'react';
import { queryUnifiedRAG } from '../utils/ragApi';

const BSA_QUESTIONS = [
  'What are BSA/AML CDD requirements?',
  'How do I document a threshold change?',
  'What did TD Bank do wrong?',
  'What are FFIEC examination procedures?',
];

const WHY_QUESTIONS = [
  'How do I write a WHY rationale for a scenario?',
  'What makes a good WHY Test response?',
  'Document alternatives considered for threshold',
];

const BOOK_QUESTIONS = [
  'What does Chapter 1 say about TD Bank?',
  'Explain the Nine Questions Framework',
  "What's in the Reconstruction Mandate about attestation?",
];

const GENERAL_QUESTIONS = [
  'What is a SAR?',
  'Explain the Nine Questions Framework',
  'Point-in-time reconstruction requirements',
];

function getSuggestedQuestions(pathname, activeElement) {
  const isBSA = pathname?.includes('banking') || pathname?.includes('bsa') || pathname?.includes('aml');
  const isWHY = activeElement?.tagName === 'TEXTAREA' && (
    activeElement.name?.toLowerCase().includes('why') ||
    activeElement.id?.toLowerCase().includes('why') ||
    activeElement.placeholder?.toLowerCase().includes('rationale')
  );
  if (isWHY) return WHY_QUESTIONS;
  if (isBSA) return BSA_QUESTIONS;
  return [...BOOK_QUESTIONS, ...GENERAL_QUESTIONS].slice(0, 5);
}

export default function AskSteadfastlyOverlay() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const inputRef = useRef(null);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const suggestedQuestions = getSuggestedQuestions(pathname, targetElement);

  const close = useCallback(() => {
    setOpen(false);
    setResult(null);
  }, []);

  const openOverlay = useCallback(() => {
    setTargetElement(typeof document !== 'undefined' ? document.activeElement : null);
    setResult(null);
    setQuery('');
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const handleOpen = () => openOverlay();
    window.addEventListener('ask-steadfastly-open', handleOpen);
    return () => window.removeEventListener('ask-steadfastly-open', handleOpen);
  }, [openOverlay]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+Shift+K or Ctrl+Shift+K to avoid conflict with CommandPalette (Cmd+K)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'K') {
        e.preventDefault();
        if (open) close();
        else openOverlay();
      }
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, openOverlay, close]);

  async function runQuery(q) {
    const trimmed = (q || query || '').trim();
    if (!trimmed) return;
    setLoading(true);
    setResult(null);
    try {
      const { answer, sources } = await queryUnifiedRAG(trimmed, []);
      setResult({ answer, sources });
    } catch (err) {
      setResult({
        answer: "We couldn't generate an answer right now. Try refining your question.",
        sources: [],
      });
    } finally {
      setLoading(false);
    }
  }

  function copyToClipboard() {
    if (!result?.answer) return;
    navigator.clipboard.writeText(result.answer);
    close();
  }

  function insertIntoField() {
    if (!result?.answer || !targetElement) return;
    const el = targetElement;
    if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const before = el.value.slice(0, start);
      const after = el.value.slice(end);
      el.value = before + result.answer + after;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.focus();
      el.setSelectionRange(before.length + result.answer.length, before.length + result.answer.length);
    }
    close();
  }

  const canInsert = targetElement && (targetElement.tagName === 'TEXTAREA' || targetElement.tagName === 'INPUT');

  if (!open) return null;

  return (
    <>
      <div
        role="presentation"
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease-out',
        }}
      />
      <div
        role="dialog"
        aria-label="Ask Steadfastly"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(480px, 100vw)',
          background: 'var(--white, #fff)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 0.25s ease-out',
        }}
      >
        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>

        <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>Ask Steadfastly</h2>
            <button type="button" onClick={close} aria-label="Close" style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', fontSize: '20px', color: '#64748b' }}>×</button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); runQuery(query); }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask a regulatory question... (⌘⇧K)"
              disabled={loading}
              style={{ width: '100%', padding: '12px 16px', fontSize: '15px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#fff' }}
              aria-label="Search"
            />
            <button type="submit" disabled={loading} style={{ marginTop: '10px', width: '100%', padding: '10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
              {loading ? 'Searching…' : 'Search'}
            </button>
          </form>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {suggestedQuestions.length > 0 && !result && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>Suggested:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setQuery(q); runQuery(q); }}
                    disabled={loading}
                    style={{ padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '999px', cursor: loading ? 'default' : 'pointer', fontSize: '13px', color: '#0f172a' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div style={{ padding: '24px 0' }}>
              <div style={{ height: '24px', background: '#e2e8f0', borderRadius: '6px', width: '80%', marginBottom: '16px' }} />
              <div style={{ height: '16px', background: '#f1f5f9', borderRadius: '4px', width: '100%', marginBottom: '8px' }} />
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '12px' }}>Searching books + blog posts…</p>
            </div>
          )}

          {!loading && result && (
            <div>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: '#334155', margin: '0 0 20px' }}>{result.answer}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <button type="button" onClick={copyToClipboard} style={{ padding: '8px 16px', fontSize: '14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer' }}>Copy to clipboard</button>
                {canInsert && (
                  <button type="button" onClick={insertIntoField} style={{ padding: '8px 16px', fontSize: '14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}>Insert into field</button>
                )}
              </div>
              {result.sources?.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#0f172a' }}>Sources</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {result.sources.map((s, i) => (
                      <a
                        key={i}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textDecoration: 'none', color: '#0f172a', fontSize: '14px' }}
                      >
                        <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', color: s.sourceType === 'book' ? '#1e40af' : '#b45309', marginBottom: '4px' }}>
                          {s.sourceType === 'book' ? `Book: ${(s.title?.split(' — ')[0] || s.book || 'Prove It').replace(/^Prove It: /, '')}` : `Blog: ${s.title}`}
                        </span>
                        <span style={{ display: 'block', fontWeight: 600 }}>{s.title}</span>
                        <span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
                          {s.sourceType === 'book' ? (s.chapter || 'Prove It') : `${s.category || 'Blog'}${s.date ? ` · ${s.date}` : ''}`}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !result && (
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              Search 302 book chunks + 3,900+ blog posts. Press <kbd style={{ fontSize: '11px', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px' }}>⌘⇧K</kbd> or <kbd style={{ fontSize: '11px', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px' }}>Ctrl+Shift+K</kbd> to toggle, or use Command Palette (⌘K) → Ask Steadfastly.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
